import { Link, useParams } from "react-router-dom";

import { useThemes } from "../data/hooks";
import { formatTimestamp } from "../lib/transcript";
import type { ThemesData, ThemeVideo } from "../lib/types";

const THEMES = [
  {
    key: "harmony",
    title: "Harmony of faiths",
    blurb:
      "Discourses on the common ground between traditions, and the one consciousness they each point toward.",
  },
  {
    key: "wellbeing",
    title: "Inner wellbeing and social cohesion",
    blurb:
      "On the mind, meditation, and a steadiness that carries into family and society.",
  },
  {
    key: "peace",
    title: "Universal peace",
    blurb:
      "On a peace that begins within a person and reaches outward to the world.",
  },
] as const;

type ThemeKey = (typeof THEMES)[number]["key"];

function listFor(themes: ThemesData, key: ThemeKey): readonly ThemeVideo[] {
  if (key === "harmony") {
    return themes.harmony;
  }
  if (key === "wellbeing") {
    return themes.wellbeing;
  }
  return themes.peace;
}

export function ThemesIndex() {
  const themes = useThemes();
  return (
    <div>
      <header className="mb-8">
        <p className="eyebrow">Themes</p>
        <h1 className="font-display text-3xl text-warm-brown">
          Three threads through the library
        </h1>
      </header>
      <div className="grid gap-5 sm:grid-cols-3">
        {THEMES.map((theme) => {
          const count =
            themes === null ? null : listFor(themes, theme.key).length;
          return (
            <Link
              key={theme.key}
              to={`/themes/${theme.key}`}
              className="plate flex flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <h2 className="font-display text-xl text-warm-brown">
                {theme.title}
              </h2>
              <p className="text-sm leading-relaxed text-warm-gray">
                {theme.blurb}
              </p>
              <span className="mt-auto pt-2 text-xs text-terra">
                {count === null ? "…" : `${count} discourses`}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function ThemeDetail() {
  const params = useParams();
  const key = params.key ?? "";
  const themes = useThemes();
  const meta = THEMES.find((theme) => theme.key === key);

  if (meta === undefined) {
    return (
      <p className="text-warm-gray">
        Unknown theme.{" "}
        <Link to="/themes" className="text-terra hover:underline">
          Back to themes
        </Link>
        .
      </p>
    );
  }

  const videos = themes === null ? null : listFor(themes, meta.key);

  return (
    <div>
      <header className="mb-8">
        <Link to="/themes" className="text-xs text-warm-gray hover:text-terra">
          Themes
        </Link>
        <h1 className="mt-1 font-display text-3xl text-warm-brown">
          {meta.title}
        </h1>
        <p className="mt-1 max-w-2xl text-warm-gray">{meta.blurb}</p>
      </header>

      {videos === null ? (
        <p className="text-warm-gray">Loading…</p>
      ) : (
        <ol className="space-y-3">
          {videos.map((video) => {
            const to =
              video.teachingT !== null
                ? `/video/${video.id}?t=${video.teachingT}`
                : `/video/${video.id}`;
            return (
              <li key={video.id} className="plate flex gap-4 p-4">
                <span className="mt-0.5 shrink-0 font-display text-lg text-terra">
                  {video.score}
                </span>
                <div className="min-w-0">
                  <Link
                    to={to}
                    className="font-display text-lg text-warm-brown hover:text-terra"
                  >
                    {video.title}
                  </Link>
                  <p className="text-sm text-warm-gray">{video.speaker}</p>
                  {video.teaching !== null ? (
                    <p className="mt-1 font-serif text-sm italic text-warm-brown-light">
                      {video.teaching}
                      {video.teachingT !== null ? (
                        <Link to={to} className="ml-2 not-italic text-terra">
                          {formatTimestamp(video.teachingT)}
                        </Link>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
